/**
 * Modal for uploading a new .docx template version — the two-phase upload
 * review flow (stage → review → promote/discard).
 *
 * Phase 1 (form): file selection + optional change notes, submitted via
 * `onUpload`, which stages the docx for review (presigned PUT + stage call)
 * without activating anything. A rejected stage (a 400 from clause-markup
 * validation, the staged-upload cap, or any other failure) does NOT advance
 * the modal: the clause-markup issues are listed in a focused role="alert"
 * region above the form so the author can fix the docx and try again. The
 * uploaded object stays in S3, unactivated.
 *
 * Phase 2 (review): on a successful stage, the modal shows the staged
 * validation warnings (if any), a "Download sample" link to the rendered
 * sample docx, and three actions — Promote (activate it now), Discard
 * (delete the staged upload), or "Review later" (closes the modal; the
 * staged upload is untouched and still visible as a row on TemplateDetail's
 * Versions card). Promote/discard errors reuse the same tagErrors/
 * genericError rendering as phase 1: a structured 400 lists clause-markup
 * issues, anything else (409 race, network) shows a generic message — and
 * the review phase stays open for retry either way.
 *
 * Phase 3 (success): once Promote succeeds, the modal shows the new version
 * number and, if the derived clause-preview refresh hit a problem during
 * activation, the `extractionWarning` returned alongside it — held open
 * until the user acknowledges it with Done, instead of auto-closing as if
 * nothing happened.
 *
 * Uses brand-compliant typography: .label class on <label> elements, text-body-sm for body text.
 */

import { useState, useRef, useEffect } from 'react';
import { Upload } from 'lucide-react';
import Modal from '../common/Modal';
import { Button } from '../common';
import TagPreview from './TagPreview';
import { getTagValidationIssues } from '../../services/api';
import type { TagIssue, StagedTemplateVersion } from '../../types/orgTemplate';

interface TemplateUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (
    file: File,
    changeNotes?: string
  ) => Promise<{ staged: StagedTemplateVersion; sampleDownloadUrl: string }>;
  onPromote: (
    stagedId: string
  ) => Promise<{ version: number; extractionWarning?: string }>;
  onDiscard: (stagedId: string) => Promise<void>;
  isUploading: boolean;
  isPromoting: boolean;
  isDiscarding: boolean;
}

export default function TemplateUploadModal({
  isOpen,
  onClose,
  onUpload,
  onPromote,
  onDiscard,
  isUploading,
  isPromoting,
  isDiscarding,
}: TemplateUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [changeNotes, setChangeNotes] = useState('');
  const [staged, setStaged] = useState<StagedTemplateVersion | null>(null);
  const [sampleDownloadUrl, setSampleDownloadUrl] = useState<string | null>(null);
  const [promotedVersion, setPromotedVersion] = useState<number | null>(null);
  const [extractionWarning, setExtractionWarning] = useState<string | null>(null);
  const [tagErrors, setTagErrors] = useState<TagIssue[]>([]);
  const [genericError, setGenericError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  // Move focus to the error region as soon as a rejected stage/promote/discard
  // populates it, so a keyboard or screen-reader user is taken straight to
  // what to fix.
  useEffect(() => {
    if (tagErrors.length > 0 || genericError) {
      alertRef.current?.focus();
    }
  }, [tagErrors, genericError]);

  const resetState = () => {
    setFile(null);
    setChangeNotes('');
    setStaged(null);
    setSampleDownloadUrl(null);
    setPromotedVersion(null);
    setExtractionWarning(null);
    setTagErrors([]);
    setGenericError(null);
  };

  const handleClose = () => {
    // Guard every close path (Cancel, Review later, the header X, backdrop,
    // Escape) while a promote/discard is in flight: abandoning mid-mutation
    // would silently drop the result — including any extractionWarning the
    // promote comes back with.
    if (isPromoting || isDiscarding) return;
    resetState();
    onClose();
  };

  const handleSubmit = async () => {
    if (!file) return;
    setTagErrors([]);
    setGenericError(null);
    try {
      const result = await onUpload(file, changeNotes || undefined);
      setStaged(result.staged);
      setSampleDownloadUrl(result.sampleDownloadUrl);
    } catch (err) {
      const issues = getTagValidationIssues(err);
      if (issues) {
        setTagErrors(issues);
        return;
      }
      setGenericError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    }
  };

  const handlePromote = async () => {
    if (!staged) return;
    setTagErrors([]);
    setGenericError(null);
    try {
      const result = await onPromote(staged.stagedId);
      setPromotedVersion(result.version);
      setExtractionWarning(result.extractionWarning ?? null);
    } catch (err) {
      const issues = getTagValidationIssues(err);
      if (issues) {
        setTagErrors(issues);
        return;
      }
      setGenericError(err instanceof Error ? err.message : 'Promote failed. Please try again.');
    }
  };

  // No confirmation prompt here, unlike the staged-row Discard on
  // TemplateDetail: the modal user uploaded this file moments ago in this
  // same session, so discarding it is a low-stakes undo of their own action.
  const handleDiscard = async () => {
    if (!staged) return;
    setTagErrors([]);
    setGenericError(null);
    try {
      await onDiscard(staged.stagedId);
      handleClose();
    } catch (err) {
      const issues = getTagValidationIssues(err);
      if (issues) {
        setTagErrors(issues);
        return;
      }
      setGenericError(err instanceof Error ? err.message : 'Discard failed. Please try again.');
    }
  };

  const errorAlert = (tagErrors.length > 0 || genericError) && (
    <div
      ref={alertRef}
      role="alert"
      tabIndex={-1}
      className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-body-sm text-red-800 dark:text-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
    >
      {genericError ? (
        <p>{genericError}</p>
      ) : (
        <>
          <p className="font-medium mb-2">
            This version was not activated. Fix the clause markup below, then re-stage.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            {tagErrors.map((issue, i) => (
              <li key={`${issue.code}-${issue.tag ?? i}`}>
                {issue.tag && (
                  <code className="font-mono">{`{${issue.tag}}`}</code>
                )}
                {issue.tag ? ' — ' : ''}
                {issue.message}
                {issue.suggestion && (
                  <span className="mt-0.5 flex items-center gap-1.5">
                    <span className="font-medium">Suggested:</span>
                    <TagPreview
                      tag={issue.suggestion}
                      variant="plain"
                      label={`suggested tag {${issue.suggestion}}`}
                    />
                  </span>
                )}
                {issue.candidates && issue.candidates.length > 0 && (
                  <ul className="list-[circle] pl-5">
                    {issue.candidates.map((c) => (
                      <li key={`${c.clauseId}-${c.optionId ?? ''}`}>
                        {c.optionId ? `${c.clauseId} / ${c.optionId}` : c.clauseId}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload Template Version">
      <div className="space-y-4">
        {promotedVersion !== null ? (
          <>
            <div
              role="status"
              className={
                extractionWarning
                  ? 'rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-body-sm text-amber-800 dark:text-amber-200'
                  : 'rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-body-sm text-emerald-800 dark:text-emerald-200'
              }
            >
              Version {promotedVersion} activated.{extractionWarning ? ` ${extractionWarning}` : ''}
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={handleClose}>Done</Button>
            </div>
          </>
        ) : staged ? (
          <>
            {errorAlert}

            {staged.tagValidation.warnings.length > 0 && (
              <div
                role="status"
                className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-body-sm text-amber-800 dark:text-amber-200"
              >
                <p className="font-medium mb-2">
                  This version has {staged.tagValidation.warnings.length} clause-markup warning
                  {staged.tagValidation.warnings.length === 1 ? '' : 's'}:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  {staged.tagValidation.warnings.map((issue, i) => (
                    <li key={`${issue.code}-${issue.tag ?? i}`}>
                      {issue.tag && <code className="font-mono">{`{${issue.tag}}`}</code>}
                      {issue.tag ? ' — ' : ''}
                      {issue.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-body-sm text-slate-500 dark:text-slate-400">
              Review the rendered sample before promoting this version to activate it.
            </p>

            <div>
              <Button
                variant="secondary"
                onClick={() => {
                  if (sampleDownloadUrl) window.open(sampleDownloadUrl, '_blank');
                }}
              >
                Download sample
              </Button>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isPromoting || isDiscarding}
              >
                Review later
              </Button>
              <Button variant="danger" onClick={handleDiscard} loading={isDiscarding}>
                Discard
              </Button>
              <Button onClick={handlePromote} loading={isPromoting}>
                Promote
              </Button>
            </div>
          </>
        ) : (
          <>
            {errorAlert}

            <div>
              <label className="label mb-2">
                Template File (.docx)
              </label>
              <div
                className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-brand-400"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                {file ? (
                  <p className="text-body-sm text-slate-900 dark:text-slate-100 font-medium">{file.name}</p>
                ) : (
                  <p className="text-body-sm text-slate-500 dark:text-slate-400">Click to select a .docx file</p>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="change-notes" className="label">
                Change Notes (optional)
              </label>
              <textarea
                id="change-notes"
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-body-sm"
                placeholder="Describe what changed in this version"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isPromoting || isDiscarding}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!file}
                loading={isUploading}
              >
                Upload
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
